/**
 * Pilotage IA global : lecture sous console:read, gestes sous console:cut-ia.
 * Le dialogue porte le brouillon et le motif ; seul le serveur publie l’état.
 * Les montants en cents sont affichés en dollars US et les motifs restent du texte.
 */
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USAGES_IA, type UsageIa } from '@artosera/shared';
import { Button, Checkbox, Dialog, Textarea } from '@artosera/ui';
import { Link } from 'react-router';
import { iaConsoleApi, type CoupureIa } from '../lib/ia.js';
import { usePeut } from '../auth/usePeut.js';
import page from '../styles/page.module.css';
import styles from './Ia.module.css';

/** Les valeurs publiées viennent du serveur ; le dialogue seul porte le brouillon. */
export function IaInterrupteurs(): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const cle = ['console', 'ia'];
  const requete = useQuery({
    queryKey: cle,
    queryFn: iaConsoleApi.globale,
    enabled: peut('console:read'),
    refetchInterval: 30_000,
  });
  const [brouillon, setBrouillon] = useState<CoupureIa | null>(null);
  const mutation = useMutation({
    mutationFn: iaConsoleApi.couper,
    onSuccess: (data) => {
      client.setQueryData(cle, data);
      setBrouillon(null);
    },
  });
  const dollars = (cents: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  const data = requete.data;
  return (
    <section className={`${page.panneau} ${styles.bloc}`} aria-labelledby="console-ia">
      <h2 id="console-ia">{t('ia.titre')}</h2>
      {requete.isPending && <p>{t('ia.chargement')}</p>}
      {requete.isError && <p role="alert">{t('ia.erreur')}</p>}
      {data && (
        <>
          <p role="status">{t(data.coupee || data.secours ? 'ia.coupee' : 'ia.active')}</p>
          {!data.lisibles && <p role="alert">{t('ia.illisible')}</p>}
          {data.secours && <p>{t('ia.secours')}</p>}
          <p>{t('ia.depense', { mois: data.mois, montant: dollars(data.depenseCents) })}</p>
          <h3>{t('ia.usagesCoupes')}</h3>
          {data.usagesCoupes.length ? (
            <ul>
              {data.usagesCoupes.map((u) => (
                <li key={u}>{t(`ia.usages.${u}`, { nsSeparator: false })}</li>
              ))}
            </ul>
          ) : (
            <p>{t('ia.aucunUsage')}</p>
          )}
          <h3>{t('ia.premieres')}</h3>
          {data.premieresGaleries.length ? (
            <ol>
              {data.premieresGaleries.map((g) => (
                <li key={g.galleryId}>
                  <Link to={`/galeries/${encodeURIComponent(g.galleryId)}/addons`}>
                    {g.galleryId}
                  </Link>{' '}
                  · {dollars(g.depenseCents)}
                </li>
              ))}
            </ol>
          ) : (
            <p>{t('ia.aucune')}</p>
          )}
          <h3>{t('ia.seuil')}</h3>
          {data.galeriesSeuil80.length ? (
            <ul>
              {data.galeriesSeuil80.map((id) => (
                <li key={id}>
                  <Link to={`/galeries/${encodeURIComponent(id)}/addons`}>{id}</Link>
                </li>
              ))}
            </ul>
          ) : (
            <p>{t('ia.aucunSeuil')}</p>
          )}
          {peut('console:cut-ia') ? (
            <Button
              disabled={!data.lisibles}
              onClick={() => {
                mutation.reset();
                setBrouillon({
                  coupee: data.coupee,
                  usagesCoupes: [...data.usagesCoupes],
                  motif: '',
                });
              }}
            >
              {t('ia.modifier')}
            </Button>
          ) : (
            <p>{t('ia.lectureSeule')}</p>
          )}
          <details>
            <summary>{t('ia.historique')}</summary>
            {data.historique.length ? (
              <ol>
                {data.historique.map((h, index) => (
                  <li key={index}>
                    <p>
                      {new Date(h.modifieLe).toLocaleString(i18n.language)} · {h.auteur}
                    </p>
                    <p>
                      {t(h.coupee ? 'ia.coupee' : 'ia.active')} · {h.motif}
                    </p>
                    <p>
                      {h.usagesCoupes
                        .map((u) => t(`ia.usages.${u}`, { nsSeparator: false }))
                        .join(', ') || t('ia.aucunUsage')}
                    </p>
                  </li>
                ))}
              </ol>
            ) : (
              <p>{t('ia.historiqueVide')}</p>
            )}
          </details>
        </>
      )}
      <Dialog
        open={brouillon !== null}
        title={t('ia.modifier')}
        closeLabel={t('ia.annuler')}
        onOpenChange={(o) => {
          if (!o && !mutation.isPending) setBrouillon(null);
        }}
      >
        {brouillon && (
          <form
            className={styles.formulaire}
            onSubmit={(e) => {
              e.preventDefault();
              if (peut('console:cut-ia') && brouillon.motif.trim() && !mutation.isPending)
                mutation.mutate(brouillon);
            }}
          >
            <fieldset disabled={mutation.isPending} className={styles.formulaire}>
              <legend>{t('ia.usagesCoupes')}</legend>
              <Checkbox
                id="ia-globale"
                label={t('ia.globale')}
                checked={brouillon.coupee}
                onCheckedChange={(v) => setBrouillon({ ...brouillon, coupee: v === true })}
              />
              {(Object.keys(USAGES_IA) as UsageIa[]).map((u) => (
                <Checkbox
                  key={u}
                  id={`ia-usage-${u}`}
                  label={t(`ia.usages.${u}`, { nsSeparator: false })}
                  checked={brouillon.usagesCoupes.includes(u)}
                  onCheckedChange={(v) =>
                    setBrouillon({
                      ...brouillon,
                      usagesCoupes: v
                        ? [...brouillon.usagesCoupes, u]
                        : brouillon.usagesCoupes.filter((x) => x !== u),
                    })
                  }
                />
              ))}
              <label htmlFor="ia-coupure-motif">{t('ia.motif')}</label>
              <Textarea
                id="ia-coupure-motif"
                required
                maxLength={500}
                value={brouillon.motif}
                onChange={(e) => setBrouillon({ ...brouillon, motif: e.target.value })}
              />
            </fieldset>
            {mutation.isError && <p role="alert">{t('ia.erreur')}</p>}
            <div className={styles.actions}>
              <Button type="submit" disabled={mutation.isPending || !brouillon.motif.trim()}>
                {t('ia.confirmer')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={mutation.isPending}
                onClick={() => setBrouillon(null)}
              >
                {t('ia.annuler')}
              </Button>
            </div>
          </form>
        )}
      </Dialog>
    </section>
  );
}
